import { useSelector } from 'react-redux';
import moment from 'moment';
import { Typography, Box, Grid } from '@mui/material';

import { FBoxContainer } from '../../base';
import { capitalizeFirstLetter, areUserRolesIn } from '../../../helpers';
import { DATE_DISPLAY_FORMAT, PayorResponsibilityEnum, Roles, Themes } from '../../../constants';
import { FORTA_GRAY4, FORTA_ERROR, FORTA_GRAY2, FORTA_WHITE } from '../../../utils/colors';

import type { IClientPolicyMap } from '../../../utils/types';
import type { RootState } from '../../../store';

function PolicyInfoRenderer({ plan }: { plan: IClientPolicyMap }) {
  return (
    <Box
      sx={{
        border: `1px solid ${FORTA_GRAY2}`,
        borderRadius: '10px',
        p: 1,
        mb: 2,
        fontSize: {
          lg: 16,
          md: 14,
        },
      }}
    >
      <Box textAlign="left" fontSize={14}>
        <Typography fontWeight={'bold'} mr={1} fontSize={14} display={'inline'}>
          Name:
        </Typography>
        <Typography display={'inline'} fontSize={14}>
          {plan.payor?.label}
        </Typography>
      </Box>
      <Box textAlign="left">
        <Typography fontWeight={'bold'} fontSize={14} mr={1} display={'inline'}>
          Policy Member ID#:
        </Typography>
        <Typography fontSize={14} display={'inline'}>
          {plan.member_id}
        </Typography>
      </Box>
      <Box textAlign="left" display="flex">
        <Box display="flex">
          <Typography fontWeight={'bold'} fontSize={14} mr={1}>
            Start date:
          </Typography>
          <Typography fontSize={14}>
            {moment(plan.date_start).format(DATE_DISPLAY_FORMAT)}
          </Typography>
        </Box>
        <Box ml={2} display="flex">
          <Typography fontSize={14} fontWeight={'bold'} mr={1}>
            End date:
          </Typography>
          <Typography fontSize={14}>
            {plan.date_end ? moment(plan.date_end).format(DATE_DISPLAY_FORMAT) : 'N/A'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function ClientInformation() {
  const { selectedClientPlans = [] } = useSelector((state: any) => state.clients);

  const { current: treatmentPlan, loading: tpLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const { recommendation, loading: recommendationLoading } = useSelector(
    (state: RootState) => state.treatmentRecommendations,
  );
  const { user } = useSelector((state: any) => state.auth);

  const tpRecommendationStartDate = recommendation?.date_start;

  // Filter logic: Only allow policies which started before the TP and
  // (does not have date_end (still active) or date_end is after TP started, means still active)
  const filteredClientPolicy = tpRecommendationStartDate
    ? selectedClientPlans.filter(
        (policy: IClientPolicyMap) =>
          moment(policy.date_start).isSameOrBefore(tpRecommendationStartDate) &&
          (!policy.date_end || moment(policy.date_end).isSameOrAfter(tpRecommendationStartDate)),
      )
    : selectedClientPlans;

  const primaryPayors: IClientPolicyMap[] = [];
  const secondaryPayors: IClientPolicyMap[] = [];
  const tertiaryPayors: IClientPolicyMap[] = [];

  filteredClientPolicy.forEach((policy: IClientPolicyMap) => {
    if (policy.payor_responsibility === PayorResponsibilityEnum.PRIMARY) {
      primaryPayors.push(policy);
    }
    if (policy.payor_responsibility === PayorResponsibilityEnum.SECONDARY) {
      secondaryPayors.push(policy);
    }
    if (policy.payor_responsibility === PayorResponsibilityEnum.TERTIARY) {
      tertiaryPayors.push(policy);
    }
  });

  return (
    <>
      <FBoxContainer
        dataTestId="clientInformationContainer"
        colorTheme={Themes.Primary}
        title="Client information"
        info={
          !areUserRolesIn(user?.roles, [Roles.Prbt]) ? `${treatmentPlan?.client?.id ?? ''}` : ''
        }
        sx={{ mt: 3 }}
      >
        {treatmentPlan?.client ? (
          <Box>
            <Box textAlign="left" display="flex" mb={2}>
              <Typography fontWeight={'bold'} mr={2}>
                Client name:
              </Typography>
              <Typography>
                {`${treatmentPlan?.client?.first_name} ${
                  treatmentPlan?.client?.middle_name ? treatmentPlan?.client?.middle_name : ''
                } ${treatmentPlan?.client?.last_name}`}
              </Typography>
            </Box>
            <Box textAlign="left" display="flex" mb={2}>
              <Typography fontWeight={'bold'} mr={2}>
                Date of birth:
              </Typography>
              <Typography>{`${moment(treatmentPlan?.client?.dob).format(
                'MM/DD/YYYY',
              )}`}</Typography>
            </Box>
            <Box textAlign="left" display="flex" mb={2}>
              <Typography fontWeight={'bold'} mr={2}>
                Gender:
              </Typography>
              <Typography>{`${capitalizeFirstLetter(treatmentPlan?.client?.sex)}`}</Typography>
            </Box>
            {primaryPayors.length + secondaryPayors.length + tertiaryPayors.length === 0 ? (
              <Typography
                textAlign="left"
                color={recommendationLoading || tpLoading ? FORTA_GRAY4 : FORTA_ERROR}
                fontWeight={700}
              >
                {recommendationLoading || tpLoading
                  ? 'Loading policies...'
                  : 'No insurance policy available.'}
              </Typography>
            ) : (
              <>
                {tpRecommendationStartDate ? (
                  <Typography textAlign="left" sx={{ mb: 2, mt: 4 }} fontWeight={700}>
                    Available insurance policies
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', mb: 2, mt: 4 }}>
                    <Typography textAlign="left" fontWeight={700}>
                      All available insurance policies.
                    </Typography>
                    <Typography textAlign="left" color={FORTA_ERROR} ml={1}>
                      This list will be updated to display active policies based on the treatment
                      recommendation date start once it&apos;s available.
                    </Typography>
                  </Box>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} lg={4}>
                    <Box
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: FORTA_WHITE,
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      data-testid="primary_policy"
                    >
                      <Typography fontWeight={700} mb={2} textAlign="left" fontSize={18}>
                        Primary:
                      </Typography>
                      {primaryPayors.length > 0 ? (
                        primaryPayors.map((plan: IClientPolicyMap) => (
                          <PolicyInfoRenderer key={plan.id} plan={plan} />
                        ))
                      ) : (
                        <Typography color={FORTA_ERROR} textAlign="left" mt={2.5} fontWeight={700}>
                          No primary insurance policy available
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <Box
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: FORTA_WHITE,
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      data-testid="secondary_policy"
                    >
                      <Typography fontWeight={700} mb={2} textAlign="left" fontSize={18}>
                        Secondary:
                      </Typography>
                      {secondaryPayors.length > 0 ? (
                        secondaryPayors.map((plan: IClientPolicyMap) => (
                          <PolicyInfoRenderer key={plan.id} plan={plan} />
                        ))
                      ) : (
                        <Typography color={FORTA_ERROR} mt={2.5} textAlign="left" fontWeight={700}>
                          No secondary insurance policy available
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} lg={4}>
                    <Box
                      sx={{
                        borderRadius: '10px',
                        backgroundColor: FORTA_WHITE,
                        p: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                      data-testid="tertiary_policy"
                    >
                      <Typography fontWeight={700} mb={2} textAlign="left" fontSize={18}>
                        Tertiary:
                      </Typography>
                      {tertiaryPayors.length > 0 ? (
                        tertiaryPayors.map((plan: IClientPolicyMap) => (
                          <PolicyInfoRenderer key={plan.id} plan={plan} />
                        ))
                      ) : (
                        <Typography color={FORTA_ERROR} mt={2.5} textAlign="left" fontWeight={700}>
                          No tertiary insurance policy available
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </>
            )}
          </Box>
        ) : (
          <></>
        )}
      </FBoxContainer>
    </>
  );
}

export default ClientInformation;
